/**
 * Excel's header/footer format-code mini-language: ONE string per band — `"&C&BStaffing&R2026-09-10"` — in
 * which `&`-codes switch section, toggle formatting and stand in for values Excel only knows at print time.
 * The PDF is a second rendering of the same workbook, so it draws that same string rather than inventing
 * page chrome of its own.
 *
 * Only the codes our own writers emit are implemented; everything else is *dropped*, argument and all — an
 * unhandled code must not reach the paper as literal `&K` text.
 */

/** A stretch of one section's text sharing a single formatting state. */
export type HeaderFooterRun = { text: string; bold: boolean };

/** Excel splits every header and footer into three independently aligned sections. */
export type HeaderFooterSections = { left: HeaderFooterRun[]; center: HeaderFooterRun[]; right: HeaderFooterRun[] };

/** What Excel substitutes for `&D`, `&T`, `&P` and `&N` on the page being drawn. */
export type HeaderFooterFields = { date: string; time: string; pageNumber: number; totalPages: number };

type SectionKey = keyof HeaderFooterSections;

const SECTION_BY_CODE: Record<string, SectionKey> = { L: "left", C: "center", R: "right" };

/** `&Kff0000` — a colour code always carries exactly six hex digits. */
const COLOR_ARGUMENT_LENGTH = 6;

const DIGIT_PATTERN = /^\d$/;

/** The codes that put characters on the page, resolved against this page's values. */
const substitutionFor = (code: string, fields: HeaderFooterFields): string | undefined => {
	switch (code) {
		case "D":
			return fields.date;
		case "T":
			return fields.time;
		case "P":
			return String(fields.pageNumber);
		case "N":
			return String(fields.totalPages);
		// Excel's escape for a literal ampersand, which is why every other code is a single character.
		case "&":
			return "&";
		default:
			return undefined;
	}
};

/** How many characters an unimplemented code takes with it. `&"Calibri,Bold"` (face), `&12` (size) and
 *  `&Kff0000` (colour) are the three that carry an argument; dropping the code but not the argument would
 *  print `Calibri,Bold"` on the page. */
const argumentLength = (source: string, argumentStart: number, code: string): number => {
	if (code === '"') {
		const close = source.indexOf('"', argumentStart);
		// An unterminated font spec is malformed; swallowing the rest beats printing it.
		return close === -1 ? source.length - argumentStart : close - argumentStart + 1;
	}
	if (code === "K") return COLOR_ARGUMENT_LENGTH;
	if (!DIGIT_PATTERN.test(code)) return 0;

	let length = 0;
	while (DIGIT_PATTERN.test(source[argumentStart + length] ?? "")) length++;
	return length;
};

/**
 * Parse one `oddHeader`/`oddFooter` string into the runs a renderer can draw; `undefined` for a sheet that
 * declares neither, so the caller draws nothing rather than substituting chrome of its own. Formatting is
 * per section — Excel's boxes are independent, so `&C&BTitle&RDate` gives a bold centre and a plain right,
 * not a bold right as a running toggle would.
 */
export const parseExcelHeaderFooter = (source: string | undefined, fields: HeaderFooterFields): HeaderFooterSections | undefined => {
	if (!source) return undefined;

	const sections: HeaderFooterSections = { left: [], center: [], right: [] };
	// DEFAULT: text before any `&L`/`&C`/`&R` belongs to the centre section, which is where a sectionless
	// header lands in Excel. Our own writers always open with a section code.
	let section: SectionKey = "center";
	let bold = false;
	let pending = "";

	const flush = (): void => {
		if (pending !== "") sections[section].push({ text: pending, bold });
		pending = "";
	};

	let index = 0;
	while (index < source.length) {
		if (source[index] !== "&") {
			pending += source[index];
			index++;
			continue;
		}

		const code = source[index + 1];
		// A trailing lone "&" is not a code and has no argument to skip; there is nothing left to read.
		if (code === undefined) break;
		index += 2;

		const substitution = substitutionFor(code, fields);
		if (substitution !== undefined) {
			pending += substitution;
			continue;
		}

		const nextSection = SECTION_BY_CODE[code];
		if (nextSection !== undefined) {
			flush();
			section = nextSection;
			bold = false;
			continue;
		}

		if (code === "B") {
			flush();
			bold = !bold;
			continue;
		}

		index += argumentLength(source, index, code);
	}
	flush();

	return sections;
};
